import { defineStore } from 'pinia';
import { computed, onMounted, ref, watchEffect, watch, reactive } from 'vue';
import { usePhoneStore } from '@/stores/usePhoneStore';
import { useI18n } from 'vue-i18n';
import { useSessionStore } from '@/stores/useSessionStore';
import { disconnectHeadset, initHeadset } from '@/libraries/headset/headset';
import { parseDataFromBrowserStorage, saveToBrowserStorage } from '@/utils/storage';

type MicProcessing = {
  autoGainControl: boolean;
  noiseSuppression: boolean;
};

export const useMediaDeviceStore = defineStore('mediaDevice', () => {
  const $session = useSessionStore();
  const $phoneStore = usePhoneStore();
  const { t } = useI18n();

  const _allDevices = ref<MediaDeviceInfo[] | null>(null);

  // Browser mic processing. All on by default, same as the browser itself.
  // Echo cancellation isn't user-facing (same as Teams/Zoom): it only matters on speaker,
  // where turning it off risks howling, so we always send it as on.
  const micProcessing = reactive({
    autoGainControl: true,
    noiseSuppression: true,
    ...parseDataFromBrowserStorage<Partial<MicProcessing>>('micProcessing'),
  });

  function getAudioLabel(audioId: string | null, kind: 'audioinput' | 'audiooutput') {
    const lookupId = audioId ?? 'default';
    const audioDevice = _allDevices.value?.filter((d) => d.kind === kind)?.find((d) => d.deviceId === lookupId);
    return audioDevice?.label || _allDevices.value?.[0]?.label;
  }

  const refreshDevices = async (): Promise<void> => {
    let devices = await navigator.mediaDevices.enumerateDevices();
    if (devices.every((info) => info.label === '')) {
      // We're in a browser like Firefox where we need to have a running media stream to get device labels. So we'll just make a running media stream.
      // This is not optimal, for example because it constantly reduces audio quality for bluetooth headsets, even if not in a call.
      await navigator.mediaDevices.getUserMedia({ audio: true });
      devices = await navigator.mediaDevices.enumerateDevices();
    }
    _allDevices.value = devices.filter((device) => !device.label.startsWith('Monitor of'));
  };

  const allMediaDevices = computed((): MediaDeviceInfo[] | null => {
    if (_allDevices.value === null) {
      refreshDevices(); // There could be multiple promises at the same time if allDevices is called multiple times, but I think that's not critical here
    }
    return _allDevices.value;
  });

  onMounted(async () => {
    await refreshDevices();
    navigator.mediaDevices.addEventListener('devicechange', refreshDevices);
  });

  // ---- AUDIO INPUT ----

  const _selectedAudioInputId = ref<string | null>(localStorage.getItem('selectedAudioInputId'));

  const audioInputDevices = computed((): MediaDeviceInfo[] | null => {
    return allMediaDevices.value === null ? null : allMediaDevices.value.filter((d) => d.kind === 'audioinput');
  });

  const hasAudioInputDefault = computed((): boolean => {
    return audioInputDevices.value ? audioInputDevices.value.some((device) => device.deviceId === 'default') : false;
  });

  const isInputDeviceAvailable = computed(() => audioInputDevices.value?.find((device) => device.deviceId === _selectedAudioInputId.value));

  const mediaInputs = computed((): { label: string; deviceId: string | null }[] | undefined => {
    const inputDevices = audioInputDevices.value;
    if (inputDevices === null) {
      return undefined;
    } else if (inputDevices.some((device: MediaDeviceInfo) => device.deviceId === 'default')) {
      return inputDevices;
    } else {
      return [{ label: t('audio.default-input').toString(), deviceId: null }, ...inputDevices];
    }
  });

  const setSelectedAudioInputId = (audioInputId: string) => {
    _selectedAudioInputId.value = audioInputId;
    $phoneStore.selectInputForAllCalls(audioInputId);
    localStorage.setItem('selectedAudioInputId', audioInputId);
  };

  const selectedAudioInputId = computed((): string | null => {
    if (_selectedAudioInputId.value !== null && !isInputDeviceAvailable.value) {
      return hasAudioInputDefault.value ? 'default' : null; // temporary display fallback only
    }
    return _selectedAudioInputId.value === null && hasAudioInputDefault.value ? 'default' : _selectedAudioInputId.value;
  });

  // Save the change, and give running calls a new mic track so it works right away (not only from the next call).
  watch(micProcessing, () => {
    saveToBrowserStorage('micProcessing', micProcessing);
    $phoneStore.selectInputForAllCalls(selectedAudioInputId.value);
  });

  // ---- AUDIO OUTPUT ----

  const _selectedAudioOutputId = ref<string | null>(localStorage.getItem('selectedAudioOutputId'));

  const audioOutputDevices = computed((): MediaDeviceInfo[] | null => {
    return allMediaDevices.value === null ? null : allMediaDevices.value.filter((d) => d.kind === 'audiooutput');
  });

  const hasAudioOutputDefault = computed((): boolean => {
    return audioOutputDevices.value ? audioOutputDevices.value.some((device) => device.deviceId === 'default') : false;
  });

  const isOutputDeviceAvailable = computed(
    () => _selectedAudioOutputId.value === null || allMediaDevices.value?.some((device) => device.deviceId === _selectedAudioOutputId.value)
  );

  const mediaOutputs = computed((): { label: string; deviceId: string | null }[] | null => {
    const outputDevices = audioOutputDevices.value;
    if (outputDevices === null || outputDevices.length === 0) {
      return null;
    } else if (outputDevices.some((device: MediaDeviceInfo) => device.deviceId === 'default')) {
      return outputDevices;
    } else {
      return [{ label: t('audio.default-output').toString(), deviceId: null }, ...outputDevices];
    }
  });

  const setSelectedAudioOutputId = (audioOutputId: string) => {
    _selectedAudioOutputId.value = audioOutputId;
    if (audioOutputId !== null) $phoneStore.selectOutputForAllCalls(audioOutputId);
    localStorage.setItem('selectedAudioOutputId', audioOutputId);
  };

  const selectedAudioOutputId = computed((): string | null => {
    if (_selectedAudioOutputId.value !== null && !isOutputDeviceAvailable.value) {
      return hasAudioOutputDefault.value ? 'default' : null; // temporary display fallback only
    }
    return _selectedAudioOutputId.value === null && hasAudioOutputDefault.value ? 'default' : _selectedAudioOutputId.value;
  });

  // ---- RINGTONE OUTPUT----

  const _selectedRingtoneOutputId = ref<string | null>(localStorage.getItem('selectedRingtoneOutputId'));
  const _ringOnBothDevices = ref(parseDataFromBrowserStorage<boolean>('ringOnBothDevices'));

  const isRingtoneDeviceAvailable = computed(
    () => _selectedRingtoneOutputId.value === null || allMediaDevices.value?.some((device) => device.deviceId === _selectedRingtoneOutputId.value)
  );

  const setSelectedRingtoneOutputId = (audioOutputId: string) => {
    _selectedRingtoneOutputId.value = audioOutputId;
    localStorage.setItem('selectedRingtoneOutputId', audioOutputId);
  };

  const selectedRingtoneOutputId = computed((): string | null => {
    if (_selectedRingtoneOutputId.value !== null && !isRingtoneDeviceAvailable.value) {
      return hasAudioOutputDefault.value ? 'default' : null; // temporary display fallback only
    }
    return _selectedRingtoneOutputId.value === null && hasAudioOutputDefault.value ? 'default' : _selectedRingtoneOutputId.value;
  });

  const ringOnBothDevices = computed({
    get: (): boolean => !!_ringOnBothDevices.value,
    set: (value: boolean) => {
      _ringOnBothDevices.value = value;
      saveToBrowserStorage('ringOnBothDevices', value);
    },
  });

  // Mic settings for getUserMedia / sip.js: chosen device + processing switches.
  const buildAudioConstraints = (inputId: string | null = selectedAudioInputId.value): MediaTrackConstraints => {
    const constraints: MediaTrackConstraints = {
      autoGainControl: micProcessing.autoGainControl,
      noiseSuppression: micProcessing.noiseSuppression,
      echoCancellation: true, // Not user-facing, see comment on micProcessing above.
    };
    // No device chosen = let the browser use its default mic.
    if (inputId !== null) {
      constraints.deviceId = { exact: inputId };
    }
    return constraints;
  };

  watchEffect(async () => {
    const inputAudioLabel = getAudioLabel(_selectedAudioInputId.value, 'audioinput');
    const outputAudioLabel = getAudioLabel(_selectedAudioOutputId.value, 'audiooutput');
    const ringtoneAudioLabel = getAudioLabel(_selectedRingtoneOutputId.value, 'audiooutput');

    const audioLabels = [inputAudioLabel, outputAudioLabel, ringtoneAudioLabel].filter((label): label is string => label != null);
    if (!audioLabels.length) return;
    if ($session._session.isAuthenticated) {
      await initHeadset(audioLabels);
    }
    if (!$session._session.isAuthenticated) {
      disconnectHeadset();
    }
  });

  return {
    selectedAudioInputId,
    setSelectedAudioInputId,
    selectedAudioOutputId,
    setSelectedAudioOutputId,
    selectedRingtoneOutputId,
    setSelectedRingtoneOutputId,
    ringOnBothDevices,
    micProcessing,
    buildAudioConstraints,
    refreshDevices,
    audioInputDevices,
    audioOutputDevices,
    hasAudioInputDefault,
    hasAudioOutputDefault,
    mediaOutputs,
    mediaInputs,
  };
});
